import React, { useState, useEffect } from "react";
import "../styles/preference.css";
import { useAuth } from "../hooks/useAuth";
import { usePreferences } from "../hooks/usePreferences";
import AuthModals from "../components/navbar/AuthModals";
import ForgotPasswordModal from "../components/preferences/ForgotPasswordModal";
import SuccessMessage from "../components/preferences/SuccessMessage";
import LoadingSpinner from "../components/preferences/LoadingSpinner";
import RatedManagersSection from "../components/preferences/RatedManagersSection";
import SavedManagersSection from "../components/preferences/SavedManagersSection";

const Preference = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const { passwordResetModal, setPasswordResetModal } = useAuth();

  const {
    userEmails,
    emailForm,
    passwordForm,
    resetPasswordForm,
    isLoadingEmails,
    isUpdatingEmail,
    isUpdatingPassword,
    isResettingPassword,
    emailUpdateSuccess,
    resetPasswordSuccess,
    passwordErrors,
    handleEmailInputChange,
    handlePasswordInputChange,
    handleResetPasswordInputChange,
    handleUpdateEmail,
    handleUpdatePassword,
    handleResetPassword,
  } = usePreferences();

  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    retypedPassword: false,
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const getActiveTabFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabFromUrl = urlParams.get("activeTab");
    if (tabFromUrl && ["profile", "rated", "saved"].includes(tabFromUrl)) {
      return tabFromUrl;
    }
    return "profile";
  };

  useEffect(() => {
    setActiveTab(getActiveTabFromUrl());

    const handleUrlChange = () => {
      const newActiveTab = getActiveTabFromUrl();
      if (newActiveTab !== activeTab) {
        setActiveTab(newActiveTab);
      }
    };

    window.addEventListener("popstate", handleUrlChange);

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(this, args);
      handleUrlChange();
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      handleUrlChange();
    };

    return () => {
      window.removeEventListener("popstate", handleUrlChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [activeTab]);

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    const url = new URL(window.location);
    url.searchParams.set("activeTab", tabName);
    window.history.pushState({}, "", url);
  };

  const handleForgotPasswordClose = () => {
    setPasswordResetModal(false);
  };

  return (
    <div className="container mt-4 preference-page">
      {/* Success Messages - Only show for email update since password uses toast */}
      {/*<SuccessMessage*/}
      {/*  message="Verification request sent to your new email."*/}
      {/*  isVisible={emailUpdateSuccess}*/}
      {/*  onClose={() => {}}*/}
      {/*/>*/}

      {/* Password Reset Modal */}
      <AuthModals
        showSignIn={false}
        showSignUp={false}
        showSuccessModal={false}
        setShowSuccessModal={() => {}}
        showModal={false}
        setShowModal={() => {}}
        message=""
        showPassword={false}
        setShowPassword={() => {}}
        showPasswordSignUp={false}
        setShowPasswordSignUp={() => {}}
        submitError=""
        onSignIn={() => {}}
        onSignUp={() => {}}
        passwordResetModal={passwordResetModal}
        setPasswordResetModal={setPasswordResetModal}
        updatePasswordModal={false}
        setUpdatePasswordModal={() => {}}
        showSignupInPasswordReset={false}
      />

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={passwordResetModal}
        onClose={handleForgotPasswordClose}
        onSubmit={handleResetPassword}
        formData={resetPasswordForm}
        onChange={handleResetPasswordInputChange}
        isLoading={isResettingPassword}
        showSuccess={resetPasswordSuccess}
      />

      <div className="row">
        <div className="col-12">
          <h2 className="mb-4">Preferences</h2>

          {/* Tab Navigation */}
          <ul className="nav nav-tabs preference-tabs" id="preferenceTabs" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "profile" ? "active" : ""}`}
                onClick={() => handleTabClick("profile")}
                type="button"
                role="tab"
                aria-selected={activeTab === "profile"}
              >
                Profile
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "rated" ? "active" : ""}`}
                onClick={() => handleTabClick("rated")}
                type="button"
                role="tab"
                aria-selected={activeTab === "rated"}
              >
                Rated Managers
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "saved" ? "active" : ""}`}
                onClick={() => handleTabClick("saved")}
                type="button"
                role="tab"
                aria-selected={activeTab === "saved"}
              >
                Saved Managers
              </button>
            </li>
          </ul>

          {/* Tab Content */}
          <div className="tab-content" id="preferenceTabContent">
            {/* Profile Tab */}
            <div
              className={`tab-pane fade ${activeTab === "profile" ? "show active" : ""}`}
              id="profile"
              role="tabpanel"
              aria-labelledby="profile-tab"
            >
              <div className="tab-content-wrapper">

                {/* Password Settings Section */}
                <div className="settings-section">
                  <h4>change password</h4>

                  <div className="form-group">
                    <label>current password</label>
                    <div className="password-input-container">
                      <input
                        type={showPasswords.currentPassword ? "text" : "password"}
                        className={`form-control ${passwordErrors.currentPassword ? "is-invalid" : ""}`}
                        placeholder="Enter current password"
                        name="currentPassword"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordInputChange}
                        disabled={isUpdatingPassword}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        aria-label="Toggle current password visibility"
                        onClick={() => togglePasswordVisibility("currentPassword")}
                        disabled={isUpdatingPassword}
                      >
                        <i className={`bi bi-${showPasswords.currentPassword ? "eye-slash" : "eye"}`}></i>
                      </button>
                    </div>
                    {passwordErrors.currentPassword && (
                      <div className="error-message">{passwordErrors.currentPassword}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>new password</label>
                    <div className="password-input-container">
                      <input
                        type={showPasswords.newPassword ? "text" : "password"}
                        className={`form-control ${passwordErrors.newPassword ? "is-invalid" : ""}`}
                        placeholder="Enter new password"
                        name="newPassword"
                        value={passwordForm.newPassword}
                        onChange={handlePasswordInputChange}
                        disabled={isUpdatingPassword}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        aria-label="Toggle new password visibility"
                        onClick={() => togglePasswordVisibility("newPassword")}
                        disabled={isUpdatingPassword}
                      >
                        <i className={`bi bi-${showPasswords.newPassword ? "eye-slash" : "eye"}`}></i>
                      </button>
                    </div>
                    {passwordErrors.newPassword && (
                      <div className="error-message">{passwordErrors.newPassword}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>retype new password</label>
                    <div className="password-input-container">
                      <input
                        type={showPasswords.retypedPassword ? "text" : "password"}
                        className={`form-control ${passwordErrors.retypedPassword ? "is-invalid" : ""}`}
                        placeholder="Retype new password"
                        name="retypedPassword"
                        value={passwordForm.retypedPassword}
                        onChange={handlePasswordInputChange}
                        disabled={isUpdatingPassword}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        aria-label="Toggle retyped password visibility"
                        onClick={() => togglePasswordVisibility("retypedPassword")}
                        disabled={isUpdatingPassword}
                      >
                        <i className={`bi bi-${showPasswords.retypedPassword ? "eye-slash" : "eye"}`}></i>
                      </button>
                    </div>
                    {passwordErrors.retypedPassword && (
                      <div className="error-message">{passwordErrors.retypedPassword}</div>
                    )}
                    {passwordErrors.match && (
                      <div className="error-message">{passwordErrors.match}</div>
                    )}
                  </div>

                  <div className="password-actions">
                    <button
                      className="btn btn-primary update-btn"
                      onClick={handleUpdatePassword}
                      disabled={
                        isUpdatingPassword ||
                        !passwordForm.currentPassword ||
                        !passwordForm.newPassword ||
                        !passwordForm.retypedPassword ||
                        Object.values(passwordErrors).some((error) => error)
                      }
                      style={{
                        borderRadius: 30,
                        background: "black",
                        outline: "none",
                        border: "none",
                        padding: "10px 20px",
                        fontWeight: 700,
                      }}
                    >
                      {isUpdatingPassword ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <span className="loading-spinner-inline"></span>
                          <span> Updating...</span>
                        </div>
                      ) : (
                        "Update password"
                      )}
                    </button>
                    <button
                      onClick={() => setPasswordResetModal(true)}
                      className="forgot-password"
                      style={{
                        background: "none",
                        border: "none",
                        color: "inherit",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      Forgot Password
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Rated Managers Tab */}
            <div
              className={`tab-pane fade ${activeTab === "rated" ? "show active" : ""}`}
              id="rated"
              role="tabpanel"
              aria-labelledby="rated-tab"
            >
              <div className="tab-content-wrapper">
                <RatedManagersSection />
              </div>
            </div>

            {/* Saved Managers Tab */}
            <div
              className={`tab-pane fade ${activeTab === "saved" ? "show active" : ""}`}
              id="saved"
              role="tabpanel"
              aria-labelledby="saved-tab"
            >
              <div className="tab-content-wrapper">
                <SavedManagersSection />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preference;